import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection, getDocs, getDoc, addDoc, updateDoc, deleteDoc,
  doc, orderBy, query, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import Nav from '../components/Nav';

const CAT_LABELS = {
  ko: { all: '전체', dev: '굴러가는중', life: '살아내기', think: '어디까지', just: '그냥' },
  en: { all: 'All', dev: 'Chugging Along', life: 'Living Through', think: 'How Far', just: 'Just' },
};
const CAT_KEYS = ['dev', 'life', 'think', 'just'];

function calcReadtime(body, lang) {
  const text = (body || '').replace(/<[^>]+>/g, '');
  const mins = Math.max(1, Math.round(text.length / 300));
  return lang === 'ko' ? `${mins}분 읽기` : `${mins} min read`;
}

/* ━━ 이미지 업로드 버튼 ━━ */
function ImageUploader({ onInsert }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const storageRef = ref(storage, `blog/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      onInsert(`<img src="${url}" alt="" style="width:100%; border-radius:2px; margin:1.5rem 0;" />`);
    } catch (e) {
      alert('업로드 실패했어요. 다시 시도해주세요.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      <input
        type="file"
        accept="image/*"
        ref={inputRef}
        style={{ display: 'none' }}
        onChange={handleUpload}
      />
      <button
        type="button"
        onClick={() => inputRef.current.click()}
        style={fs.uploadBtn}
        disabled={uploading}
      >
        {uploading ? '업로드 중...' : '📷 이미지 추가'}
      </button>
      <span style={fs.uploadHint}>클릭하면 본문 끝에 이미지가 삽입돼요</span>
    </div>
  );
}

/* ━━ 글 작성/수정 폼 ━━ */
function PostForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState(initial || {
    cat: 'dev',
    date: new Date().toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' }),
    series_ko: '', series_en: '',
    title_ko: '', title_en: '',
    summary_ko: '', summary_en: '',
    body_ko: '', body_en: '',
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleImageInsert = (imgTag) => {
    set('body_ko', form.body_ko + '\n' + imgTag);
  };

  const handleSave = () => {
    if (!form.title_ko || !form.body_ko) {
      alert('제목과 본문(한국어)은 필수예요.');
      return;
    }
    onSave(form);
  };

  return (
    <div style={fs.wrap}>
      <h2 style={fs.heading}>{initial ? '글 수정' : '새 글 쓰기'}</h2>

      <div style={fs.row}>
        <label style={fs.label}>카테고리</label>
        <select value={form.cat} onChange={e => set('cat', e.target.value)} style={fs.input}>
          {CAT_KEYS.map(k => <option key={k} value={k}>{CAT_LABELS.ko[k]}</option>)}
        </select>
      </div>

      <div style={fs.row}>
        <label style={fs.label}>날짜</label>
        <input value={form.date} onChange={e => set('date', e.target.value)} style={fs.input} />
      </div>

      <div style={fs.row}>
        <label style={fs.label}>시리즈 (한)</label>
        <input placeholder="예: ImIn 개발기 #2" value={form.series_ko} onChange={e => set('series_ko', e.target.value)} style={fs.input} />
      </div>
      <div style={fs.row}>
        <label style={fs.label}>시리즈 (영)</label>
        <input placeholder="예: Building ImIn #2" value={form.series_en} onChange={e => set('series_en', e.target.value)} style={fs.input} />
      </div>

      <div style={fs.row}>
        <label style={fs.label}>제목 (한) *</label>
        <input value={form.title_ko} onChange={e => set('title_ko', e.target.value)} style={fs.input} />
      </div>
      <div style={fs.row}>
        <label style={fs.label}>제목 (영)</label>
        <input value={form.title_en} onChange={e => set('title_en', e.target.value)} style={fs.input} />
      </div>

      <div style={fs.row}>
        <label style={fs.label}>요약 (한)</label>
        <input value={form.summary_ko} onChange={e => set('summary_ko', e.target.value)} style={fs.input} />
      </div>
      <div style={fs.row}>
        <label style={fs.label}>요약 (영)</label>
        <input value={form.summary_en} onChange={e => set('summary_en', e.target.value)} style={fs.input} />
      </div>

      <div style={fs.row}>
        <label style={fs.label}>본문 (한) *</label>
        <ImageUploader onInsert={handleImageInsert} />
        <textarea
          value={form.body_ko}
          onChange={e => set('body_ko', e.target.value)}
          style={fs.textarea}
          rows={14}
          placeholder={'<p>내용을 입력하세요</p>\n<h2>소제목</h2>\n<p>내용</p>'}
        />
      </div>
      <div style={fs.row}>
        <label style={fs.label}>본문 (영)</label>
        <textarea value={form.body_en} onChange={e => set('body_en', e.target.value)} style={fs.textarea} rows={14} />
      </div>

      <div style={fs.btnRow}>
        <button onClick={onCancel} style={fs.cancelBtn}>취소</button>
        <button onClick={handleSave} style={fs.saveBtn}>저장</button>
      </div>
    </div>
  );
}

/* ━━ 댓글 섹션 ━━ */
function CommentSection({ postId, lang, user }) {
  const [comments, setComments] = useState([]);
  const [text, setText]         = useState('');
  const [name, setName]         = useState('');
  const [replyTo, setReplyTo]   = useState(null); // { id, name }
  const [replyText, setReplyText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = async () => {
    const q = query(
      collection(db, 'posts', postId, 'comments'),
      orderBy('createdAt', 'asc')
    );
    const snap = await getDocs(q);
    setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => { fetchComments(); }, [postId]);

  // 댓글 작성
  const handleSubmit = async () => {
    if (!text.trim()) return;
    if (!name.trim()) { alert(lang === 'ko' ? '이름을 입력해주세요.' : 'Please enter your name.'); return; }
    setSubmitting(true);
    await addDoc(collection(db, 'posts', postId, 'comments'), {
      name: name.trim(),
      text: text.trim(),
      createdAt: serverTimestamp(),
      replies: [],
    });
    setText('');
    await fetchComments();
    setSubmitting(false);
  };

  // 답글 작성
  const handleReply = async (commentId) => {
    if (!replyText.trim()) return;
    const commentRef = doc(db, 'posts', postId, 'comments', commentId);
    const snap = await getDoc(commentRef);
    const existing = snap.data().replies || [];
    await updateDoc(commentRef, {
      replies: [...existing, {
        text: replyText.trim(),
        createdAt: new Date().toISOString(),
      }]
    });
    setReplyText('');
    setReplyTo(null);
    await fetchComments();
  };

  // 댓글 삭제 (관리자)
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm(lang === 'ko' ? '댓글을 삭제할까요?' : 'Delete this comment?')) return;
    await deleteDoc(doc(db, 'posts', postId, 'comments', commentId));
    await fetchComments();
  };

  const t = {
    title:       lang === 'ko' ? '댓글' : 'Comments',
    namePlaceholder: lang === 'ko' ? '이름' : 'Name',
    textPlaceholder: lang === 'ko' ? '댓글을 남겨주세요.' : 'Leave a comment.',
    submit:      lang === 'ko' ? '등록' : 'Submit',
    reply:       lang === 'ko' ? '답글' : 'Reply',
    replyPlaceholder: lang === 'ko' ? '답글을 입력하세요.' : 'Write a reply.',
    replySubmit: lang === 'ko' ? '답글 등록' : 'Reply',
    cancel:      lang === 'ko' ? '취소' : 'Cancel',
    empty:       lang === 'ko' ? '첫 번째 댓글을 남겨보세요.' : 'Be the first to comment.',
    delete:      lang === 'ko' ? '삭제' : 'Delete',
  };

  return (
    <div style={cs.wrap}>
      <h3 style={cs.title}>{t.title} {comments.length > 0 && <span style={cs.count}>{comments.length}</span>}</h3>

      {/* 댓글 목록 */}
      {comments.length === 0
        ? <p style={cs.empty}>{t.empty}</p>
        : comments.map(c => (
          <div key={c.id} style={cs.comment}>
            <div style={cs.commentHeader}>
              <span style={cs.commentName}>{c.name}</span>
              <div style={cs.commentMeta}>
                <span style={cs.commentDate}>
                  {c.createdAt?.toDate?.()?.toLocaleDateString('ko-KR') || ''}
                </span>
                {/* 관리자 답글/삭제 */}
                {user && (
                  <>
                    <button style={cs.metaBtn} onClick={() => setReplyTo(replyTo?.id === c.id ? null : { id: c.id, name: c.name })}>
                      {t.reply}
                    </button>
                    <button style={{ ...cs.metaBtn, color: 'var(--ink-pale)' }} onClick={() => handleDeleteComment(c.id)}>
                      {t.delete}
                    </button>
                  </>
                )}
              </div>
            </div>
            <p style={cs.commentText}>{c.text}</p>

            {/* 답글 목록 */}
            {c.replies?.map((r, i) => (
              <div key={i} style={cs.reply}>
                <span style={cs.replyLabel}>너풀너풀 스튜디오</span>
                <p style={cs.replyText}>{r.text}</p>
              </div>
            ))}

            {/* 답글 입력 (관리자) */}
            {user && replyTo?.id === c.id && (
              <div style={cs.replyForm}>
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder={t.replyPlaceholder}
                  style={cs.replyInput}
                  rows={3}
                />
                <div style={cs.replyBtns}>
                  <button style={cs.cancelBtn} onClick={() => { setReplyTo(null); setReplyText(''); }}>{t.cancel}</button>
                  <button style={cs.submitBtn} onClick={() => handleReply(c.id)}>{t.replySubmit}</button>
                </div>
              </div>
            )}
          </div>
        ))
      }

      {/* 댓글 입력 (누구나) */}
      <div style={cs.form}>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={t.namePlaceholder}
          style={cs.nameInput}
        />
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={t.textPlaceholder}
          style={cs.textInput}
          rows={4}
        />
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={cs.submitBtn}
        >
          {submitting ? '...' : t.submit}
        </button>
      </div>
    </div>
  );
}

/* ━━ 메인 Blog 컴포넌트 ━━ */
export default function Blog() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [lang, setLang]       = useState('ko');
  const [posts, setPosts]     = useState([]);
  const [post, setPost]       = useState(null);
  const [cat, setCat]         = useState('all');
  const [loading, setLoading] = useState(true);
  const [mode, setMode]       = useState(null);

  const fetchPosts = async () => {
    try {
      const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchPosts(); }, []);

  useEffect(() => {
    if (!id) { setPost(null); return; }
    const fetch = async () => {
      const snap = await getDoc(doc(db, 'posts', id));
      if (snap.exists()) setPost({ id: snap.id, ...snap.data() });
    };
    fetch();
  }, [id]);

  const handleSave = async (form) => {
    const data = {
      cat: form.cat,
      date: form.date,
      series: (form.series_ko || form.series_en) ? { ko: form.series_ko, en: form.series_en } : null,
      title:   { ko: form.title_ko,   en: form.title_en },
      summary: { ko: form.summary_ko, en: form.summary_en },
      body:    { ko: form.body_ko,    en: form.body_en },
    };
    if (mode === 'new') {
      data.createdAt = serverTimestamp();
      const r = await addDoc(collection(db, 'posts'), data);
      await fetchPosts();
      setMode(null);
      navigate(`/blog/${r.id}`);
    } else if (mode === 'edit' && post) {
      await updateDoc(doc(db, 'posts', post.id), data);
      await fetchPosts();
      setMode(null);
      navigate(`/blog/${post.id}`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('정말 삭제할까요?')) return;
    await deleteDoc(doc(db, 'posts', post.id));
    await fetchPosts();
    navigate('/blog');
  };

  const toFormInitial = (p) => ({
    cat: p.cat, date: p.date,
    series_ko: p.series?.ko || '', series_en: p.series?.en || '',
    title_ko: p.title?.ko || '', title_en: p.title?.en || '',
    summary_ko: p.summary?.ko || '', summary_en: p.summary?.en || '',
    body_ko: p.body?.ko || '', body_en: p.body?.en || '',
  });

  const filtered = cat === 'all' ? posts : posts.filter(p => p.cat === cat);

  // ── 글 작성/수정 폼 ─────────────────
  if (mode === 'new' || mode === 'edit') {
    return (
      <>
        <Nav lang={lang} setLang={setLang} />
        <PostForm
          initial={mode === 'edit' && post ? toFormInitial(post) : null}
          onSave={handleSave}
          onCancel={() => setMode(null)}
        />
        <Footer lang={lang} />
      </>
    );
  }

  // ── 글 상세 ─────────────────────────
  if (id && post) {
    const idx  = posts.findIndex(p => p.id === id);
    const prev = posts[idx + 1];
    const next = posts[idx - 1];

    return (
      <>
        <Nav lang={lang} setLang={setLang} />
        <div style={s.articleMain}>

          <div style={s.articleTopRow}>
            <button style={s.backBtn} onClick={() => navigate('/blog')}>
              {lang === 'ko' ? '← 목록으로' : '← Back'}
            </button>
            {user && (
              <div style={s.adminBtns}>
                <button style={s.editBtn} onClick={() => setMode('edit')}>수정</button>
                <button style={s.deleteBtn} onClick={handleDelete}>삭제</button>
              </div>
            )}
          </div>

          <header style={s.articleHeader}>
            <span style={s.articleCat}>{CAT_LABELS[lang][post.cat]}</span>
            {post.series && <span style={s.articleSeries}>{post.series[lang]}</span>}
            <h1 style={s.articleTitle}>{post.title?.[lang]}</h1>
            <p style={s.articleByline}>
              {post.date} · {calcReadtime(post.body?.[lang], lang)}
            </p>
          </header>

          <div style={s.articleBody} dangerouslySetInnerHTML={{ __html: post.body?.[lang] || '' }} />

          {/* 댓글 */}
          <CommentSection postId={post.id} lang={lang} user={user} />

          <div style={s.articleEnd}>
            <div style={s.articleNav}>
              <button
                style={{ ...s.navBtn, opacity: prev ? 1 : .3 }}
                disabled={!prev}
                onClick={() => prev && navigate(`/blog/${prev.id}`)}
              >
                <span style={s.navDir}>{lang === 'ko' ? '← 이전 글' : '← Previous'}</span>
                <span style={s.navTitle}>{prev ? prev.title?.[lang] : (lang === 'ko' ? '첫 번째 글이에요' : 'First post')}</span>
              </button>
              <button
                style={{ ...s.navBtn, ...s.navBtnRight, opacity: next ? 1 : .3 }}
                disabled={!next}
                onClick={() => next && navigate(`/blog/${next.id}`)}
              >
                <span style={s.navDir}>{lang === 'ko' ? '다음 글 →' : 'Next →'}</span>
                <span style={s.navTitle}>{next ? next.title?.[lang] : (lang === 'ko' ? '가장 최근 글이에요' : 'Latest post')}</span>
              </button>
            </div>
            <div style={s.articleEndFooter}>
              <button style={s.endBack} onClick={() => navigate('/blog')}>
                {lang === 'ko' ? '← 목록으로' : '← Back'}
              </button>
              <span style={s.endTag}>
                {lang === 'ko' ? '작은 것들을 꾸준히' : 'Small things, steadily'}
              </span>
            </div>
          </div>

        </div>
        <Footer lang={lang} />
      </>
    );
  }

  // ── 글 목록 ─────────────────────────
  return (
    <>
      <Nav lang={lang} setLang={setLang} />
      <div style={s.blogMain}>
        <div style={s.blogHeader}>
          <h2 style={s.blogTitle}>{lang === 'ko' ? '일지' : 'Journal'}</h2>
          <div style={s.catFilter}>
            {Object.entries(CAT_LABELS[lang]).map(([key, label]) => (
              <button
                key={key}
                style={{ ...s.catBtn, ...(cat === key ? s.catBtnActive : {}) }}
                onClick={() => setCat(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {user && (
          <button style={s.newPostBtn} onClick={() => setMode('new')}>
            + 새 글 쓰기
          </button>
        )}

        {loading && <p style={s.loading}>불러오는 중...</p>}

        <div>
          {filtered.map(p => (
            <div key={p.id} style={s.postItem} onClick={() => navigate(`/blog/${p.id}`)}>
              <div style={s.postMeta}>
                <div style={s.postDate}>{p.date}</div>
                <span style={s.postCat}>{CAT_LABELS[lang][p.cat]}</span>
                {p.series && <span style={s.postSeries}>{p.series[lang]}</span>}
                <span style={s.postReadtime}>{calcReadtime(p.body?.[lang], lang)}</span>
              </div>
              <div>
                <div style={s.postTitle}>{p.title?.[lang]}</div>
                <p style={s.postSummary}>{p.summary?.[lang]}</p>
                <span style={s.readMore}>{lang === 'ko' ? '읽기 →' : 'Read →'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer lang={lang} />
    </>
  );
}

function Footer({ lang }) {
  return (
    <footer style={s.footer}>
      <span style={s.footerL}>{lang === 'ko' ? '© 2025 너풀너풀 스튜디오' : '© 2025 Nuhpul Studio'}</span>
      <span style={s.footerR}>{lang === 'ko' ? '작은 것들을 꾸준히' : 'Small things, steadily'}</span>
    </footer>
  );
}

const s = {
  blogMain: { maxWidth: '960px', margin: '0 auto', padding: '0 3rem 6rem' },
  blogHeader: { paddingTop: '3.5rem', paddingBottom: '2rem', borderBottom: '1px solid var(--ink-pale)', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' },
  blogTitle: { fontFamily: 'var(--serif)', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: 200, fontStyle: 'italic', color: 'var(--ink)' },
  catFilter: { display: 'flex', gap: '.6rem', flexWrap: 'wrap' },
  catBtn: { fontFamily: 'var(--mono)', fontSize: '.58rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', background: 'none', border: '1px solid var(--ink-pale)', padding: '.22rem .7rem', borderRadius: '20px', cursor: 'pointer' },
  catBtnActive: { color: 'var(--paper)', background: 'var(--ink)', borderColor: 'var(--ink)' },
  newPostBtn: { fontFamily: 'var(--mono)', fontSize: '.6rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink)', background: 'none', border: '1px solid var(--ink)', padding: '.35rem 1rem', borderRadius: '2px', cursor: 'pointer', marginTop: '2rem', display: 'block' },
  loading: { fontFamily: 'var(--mono)', fontSize: '.7rem', color: 'var(--ink-soft)', marginTop: '3rem' },
  postItem: { display: 'grid', gridTemplateColumns: '100px 1fr', gap: '2.5rem', padding: '2rem 0', borderBottom: '1px solid var(--ink-pale)', cursor: 'pointer' },
  postMeta: { paddingTop: '.2rem' },
  postDate: { fontFamily: 'var(--mono)', fontSize: '.6rem', letterSpacing: '.08em', color: 'var(--ink-soft)' },
  postCat: { fontFamily: 'var(--mono)', fontSize: '.55rem', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink-pale)', marginTop: '.35rem', display: 'block' },
  postSeries: { fontFamily: 'var(--mono)', fontSize: '.55rem', letterSpacing: '.08em', color: 'var(--ink-pale)', marginTop: '.25rem', display: 'block' },
  postReadtime: { fontFamily: 'var(--mono)', fontSize: '.55rem', letterSpacing: '.08em', color: 'var(--ink-pale)', marginTop: '.25rem', display: 'block' },
  postTitle: { fontFamily: 'var(--serif)', fontSize: '1.15rem', fontWeight: 300, color: 'var(--ink)', lineHeight: 1.4, letterSpacing: '-.01em' },
  postSummary: { fontSize: '.88rem', fontWeight: 300, lineHeight: 1.9, color: 'var(--ink-soft)', marginTop: '.5rem' },
  readMore: { fontFamily: 'var(--mono)', fontSize: '.58rem', letterSpacing: '.1em', color: 'var(--ink-soft)', marginTop: '.8rem', display: 'inline-block' },
  articleMain: { maxWidth: '680px', margin: '0 auto', padding: '0 3rem 6rem' },
  articleTopRow: { paddingTop: '2.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  backBtn: { fontFamily: 'var(--mono)', fontSize: '.65rem', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink-soft)', background: 'none', border: 'none', cursor: 'pointer', padding: '.4rem 0', minHeight: '44px', display: 'flex', alignItems: 'center' },
  adminBtns: { display: 'flex', gap: '.6rem' },
  editBtn: { fontFamily: 'var(--mono)', fontSize: '.65rem', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink)', background: 'none', border: '1px solid var(--ink-pale)', padding: '.5rem 1rem', borderRadius: '2px', cursor: 'pointer', minHeight: '44px' },
  deleteBtn: { fontFamily: 'var(--mono)', fontSize: '.65rem', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink-soft)', background: 'none', border: '1px solid var(--ink-pale)', padding: '.5rem 1rem', borderRadius: '2px', cursor: 'pointer', minHeight: '44px' },
  articleHeader: { paddingTop: '2.8rem', paddingBottom: '2.5rem', borderBottom: '1px solid var(--ink-pale)' },
  articleCat: { fontFamily: 'var(--mono)', fontSize: '.56rem', letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: '.6rem', display: 'block' },
  articleSeries: { fontFamily: 'var(--mono)', fontSize: '.56rem', letterSpacing: '.1em', color: 'var(--ink-pale)', marginBottom: '.8rem', display: 'block' },
  articleTitle: { fontFamily: 'var(--serif)', fontSize: 'clamp(1.7rem, 3.5vw, 2.6rem)', fontWeight: 200, lineHeight: 1.25, letterSpacing: '-.025em', color: 'var(--ink)' },
  articleByline: { marginTop: '1.2rem', fontFamily: 'var(--mono)', fontSize: '.6rem', letterSpacing: '.08em', color: 'var(--ink-soft)' },
  articleBody: { paddingTop: '2.5rem', fontSize: '1rem', fontWeight: 300, lineHeight: 2.1, color: 'var(--ink-mid)' },
  articleEnd: { marginTop: '4rem', paddingTop: '2rem', borderTop: '1px solid var(--ink-pale)' },
  articleNav: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1px', background: 'var(--ink-pale)', border: '1px solid var(--ink-pale)', marginBottom: '1.5rem' },
  navBtn: { background: 'var(--paper)', padding: '1.4rem 1.6rem', cursor: 'pointer', border: 'none', textAlign: 'left' },
  navBtnRight: { textAlign: 'right' },
  navDir: { fontFamily: 'var(--mono)', fontSize: '.55rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', display: 'block', marginBottom: '.4rem' },
  navTitle: { fontFamily: 'var(--serif)', fontSize: '.92rem', fontWeight: 300, color: 'var(--ink)', lineHeight: 1.35 },
  articleEndFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  endBack: { fontFamily: 'var(--mono)', fontSize: '.65rem', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink-soft)', background: 'none', border: 'none', cursor: 'pointer', minHeight: '44px', padding: '.4rem 0' },
  endTag: { fontFamily: 'var(--serif)', fontSize: '.85rem', fontStyle: 'italic', color: 'var(--ink-pale)' },
  footer: { borderTop: '1.5px solid var(--ink)', padding: '.6rem 3rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  footerL: { fontFamily: 'var(--mono)', fontSize: '.58rem', letterSpacing: '.08em', color: 'var(--ink-soft)' },
  footerR: { fontFamily: 'var(--serif)', fontSize: '.8rem', fontStyle: 'italic', color: 'var(--ink-soft)' },
};

const fs = {
  wrap: { maxWidth: '680px', margin: '0 auto', padding: '3rem 3rem 6rem' },
  heading: { fontFamily: 'var(--serif)', fontSize: '1.6rem', fontWeight: 200, fontStyle: 'italic', color: 'var(--ink)', marginBottom: '2.5rem' },
  row: { display: 'flex', flexDirection: 'column', gap: '.4rem', marginBottom: '1.4rem' },
  label: { fontFamily: 'var(--mono)', fontSize: '.58rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-soft)' },
  input: { fontFamily: 'var(--serif)', fontSize: '.95rem', fontWeight: 300, color: 'var(--ink)', background: 'var(--paper-dim)', border: '1px solid var(--ink-pale)', borderRadius: '2px', padding: '.7rem 1rem', outline: 'none' },
  textarea: { fontFamily: 'var(--serif)', fontSize: '.95rem', fontWeight: 300, color: 'var(--ink)', background: 'var(--paper-dim)', border: '1px solid var(--ink-pale)', borderRadius: '2px', padding: '.7rem 1rem', outline: 'none', resize: 'vertical', lineHeight: 1.8 },
  uploadBtn: { fontFamily: 'var(--mono)', fontSize: '.6rem', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink)', background: 'none', border: '1px solid var(--ink-pale)', padding: '.35rem .9rem', borderRadius: '2px', cursor: 'pointer', marginBottom: '.5rem' },
  uploadHint: { fontFamily: 'var(--mono)', fontSize: '.55rem', letterSpacing: '.08em', color: 'var(--ink-pale)', marginLeft: '.8rem' },
  btnRow: { display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '2rem' },
  cancelBtn: { fontFamily: 'var(--mono)', fontSize: '.62rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', background: 'none', border: '1px solid var(--ink-pale)', padding: '.7rem 1.6rem', borderRadius: '2px', cursor: 'pointer' },
  saveBtn: { fontFamily: 'var(--mono)', fontSize: '.62rem', letterSpacing: '.14em', textTransform: 'uppercase', color: 'var(--paper)', background: 'var(--ink)', border: 'none', padding: '.7rem 1.6rem', borderRadius: '2px', cursor: 'pointer' },
};

const cs = {
  wrap: { marginTop: '4rem', paddingTop: '3rem', borderTop: '1px solid var(--ink-pale)' },
  title: { fontFamily: 'var(--serif)', fontSize: '1.2rem', fontWeight: 300, color: 'var(--ink)', marginBottom: '2rem', display: 'flex', alignItems: 'center', gap: '.6rem' },
  count: { fontFamily: 'var(--mono)', fontSize: '.65rem', color: 'var(--ink-soft)' },
  empty: { fontFamily: 'var(--mono)', fontSize: '.7rem', letterSpacing: '.1em', color: 'var(--ink-pale)', marginBottom: '2rem' },
  comment: { borderBottom: '1px solid var(--ink-pale)', paddingBottom: '1.8rem', marginBottom: '1.8rem' },
  commentHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '.6rem' },
  commentName: { fontFamily: 'var(--mono)', fontSize: '.75rem', letterSpacing: '.08em', color: 'var(--ink)', fontWeight: 400 },
  commentMeta: { display: 'flex', gap: '.6rem', alignItems: 'center' },
  commentDate: { fontFamily: 'var(--mono)', fontSize: '.65rem', letterSpacing: '.06em', color: 'var(--ink-pale)' },
  metaBtn: { fontFamily: 'var(--mono)', fontSize: '.65rem', letterSpacing: '.08em', color: 'var(--ink-soft)', background: 'none', border: 'none', cursor: 'pointer', padding: '.4rem .6rem', minHeight: '44px', display: 'flex', alignItems: 'center' },
  commentText: { fontSize: '1rem', fontWeight: 300, lineHeight: 1.9, color: 'var(--ink-mid)' },
  reply: { marginTop: '1.2rem', paddingLeft: '1.2rem', borderLeft: '2px solid var(--ink-pale)' },
  replyLabel: { fontFamily: 'var(--mono)', fontSize: '.62rem', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink-soft)', display: 'block', marginBottom: '.3rem' },
  replyText: { fontSize: '.95rem', fontWeight: 300, lineHeight: 1.9, color: 'var(--ink-mid)' },
  replyForm: { marginTop: '1rem', paddingLeft: '1.2rem' },
  replyInput: { width: '100%', fontFamily: 'var(--serif)', fontSize: '1rem', fontWeight: 300, color: 'var(--ink)', background: 'var(--paper-dim)', border: '1px solid var(--ink-pale)', borderRadius: '2px', padding: '.8rem 1rem', outline: 'none', resize: 'vertical', lineHeight: 1.8 },
  replyBtns: { display: 'flex', gap: '.6rem', justifyContent: 'flex-end', marginTop: '.6rem' },
  form: { marginTop: '2.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' },
  nameInput: { fontFamily: 'var(--serif)', fontSize: '1rem', fontWeight: 300, color: 'var(--ink)', background: 'var(--paper-dim)', border: '1px solid var(--ink-pale)', borderRadius: '2px', padding: '.9rem 1rem', outline: 'none', width: '100%', minHeight: '44px' },
  textInput: { fontFamily: 'var(--serif)', fontSize: '1rem', fontWeight: 300, color: 'var(--ink)', background: 'var(--paper-dim)', border: '1px solid var(--ink-pale)', borderRadius: '2px', padding: '.9rem 1rem', outline: 'none', resize: 'vertical', lineHeight: 1.8 },
  cancelBtn: { fontFamily: 'var(--mono)', fontSize: '.65rem', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--ink-soft)', background: 'none', border: '1px solid var(--ink-pale)', padding: '.7rem 1.2rem', borderRadius: '2px', cursor: 'pointer', minHeight: '44px' },
  submitBtn: { fontFamily: 'var(--mono)', fontSize: '.65rem', letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--paper)', background: 'var(--ink)', border: 'none', padding: '.7rem 1.6rem', borderRadius: '2px', cursor: 'pointer', alignSelf: 'flex-end', minHeight: '44px' },
};
